import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Button from '../components/Button';

const CustomerGuide = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>Customer Moving Guide – MyMoveAdvisor</title>
                <meta name="description" content="Your complete guide to hiring a mover in Canada. Learn what to expect, how to compare quotes, and get tips for a smooth move." />
                <meta property="og:title" content="Customer Moving Guide – MyMoveAdvisor" />
                <meta property="og:description" content="Your complete guide to hiring a mover in Canada." />
                <link rel="canonical" href="https://mymoveadvisor.ca/customer-guide" />
            </Helmet>
            {/* Header Section */}
            <div className="bg-gray-900 border-b border-white/10 pt-32 pb-20 overflow-hidden relative">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-green-500/5 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-3xl">
                        <nav className="flex mb-8 text-sm font-medium">
                            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
                            <span className="text-gray-600 mx-3">/</span>
                            <span className="text-green-500">Customer Resources</span>
                        </nav>
                        <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
                            Customer <span className="text-green-500 text-outline-green">Guide</span>
                        </h1>
                        <p className="text-xl text-gray-400 leading-relaxed">
                            Expert advice, step-by-step instructions, and efficiency hacks to help you find the perfect moving partner.
                        </p>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-16">
                <div className="grid lg:grid-cols-3 gap-12">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2 space-y-16">
                        {/* Main Guide Article */}
                        <article className="bg-white rounded-[2.5rem] overflow-hidden border border-gray-100 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)]">
                            <div className="relative h-96">
                                <img
                                    src="https://images.pexels.com/photos/4246120/pexels-photo-4246120.jpeg?auto=compress&cs=tinysrgb&w=1200"
                                    alt="Customer Quote Guide"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                                <div className="absolute bottom-8 left-8 right-8">
                                    <div className="flex gap-3 mb-4">
                                        <span className="bg-green-600 text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">Step-by-Step Guide</span>
                                        <span className="bg-white/10 backdrop-blur-md text-white text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider">8 Min Read</span>
                                    </div>
                                    <h2 className="text-3xl md:text-4xl font-black text-white leading-tight">
                                        How to Secure the Best Moving Quotes: A Step-by-Step Guide
                                    </h2>
                                </div>
                            </div>

                            <div className="p-10 md:p-14">
                                <div className="prose prose-lg prose-green max-w-none text-gray-600 space-y-8">
                                    <p className="text-xl font-medium text-gray-900 leading-relaxed italic border-l-4 border-green-500 pl-6">
                                        Ready to find your perfect mover? We've simplified the process of finding the perfect partner into a few easy steps. Follow this guide to get the most accurate quotes.
                                    </p>

                                    <section className="space-y-6">
                                        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                                            <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center font-black">1</span>
                                            Inventory Your Home
                                        </h3>
                                        <p>
                                            Before clicking "Get Quote," take a quick tour of your home. Note down large furniture items, any specialty pieces (like a piano or glass cabinet), and an estimated count of boxes. Accuracy here leads to the most reliable final price.
                                        </p>
                                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 text-sm">
                                            <strong className="text-gray-900 block mb-2 font-bold">💡 Pro Tip:</strong>
                                            Don't forget the attic and the garage! These areas are often overlooked during initial estimates.
                                        </div>
                                    </section>

                                    <section className="space-y-6 pt-6 border-t border-gray-50">
                                        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                                            <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center font-black">2</span>
                                            Submit Your Request
                                        </h3>
                                        <p>
                                            Head over to our <Link to="/quote" className="text-green-600 font-bold hover:underline">Quote Request Form</Link>. Provide your destination zip codes, desired moving dates, and the home inventory details you gathered.
                                        </p>
                                    </section>

                                    <section className="space-y-6 pt-6 border-t border-gray-50">
                                        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                                            <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center font-black">3</span>
                                            Review Vetted Offers
                                        </h3>
                                        <p>
                                            Within minutes, you'll receive multiple vetted offers. Look beyond just the price—pay close attention to mover ratings, recent customer reviews, and their specific experience with residential or commercial moves.
                                        </p>
                                    </section>

                                    <div className="bg-green-600 rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-8 shadow-2xl shadow-green-900/20 mt-12">
                                        <div className="flex-1 text-center md:text-left">
                                            <h4 className="text-2xl font-bold text-white mb-2">Ready to start?</h4>
                                            <p className="text-green-50/80">Get up to 5 competitive quotes from verified movers in under 2 minutes.</p>
                                        </div>
                                        <Link to="/quote">
                                            <button className="px-10 py-4 bg-white text-green-700 hover:bg-green-50 shadow-xl font-bold rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95">
                                                Request My Quotes
                                            </button>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-8">
                        <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm leading-relaxed">
                            <h4 className="text-lg font-bold text-gray-900 mb-6">Related Resources</h4>
                            <div className="space-y-6 text-sm">
                                {[
                                    { title: 'The Ultimate Moving Checklist', category: 'Planning' },
                                    { title: 'How to Pack Fragile Items', category: 'Packing' },
                                    { title: 'Choosing the Right Moving Date', category: 'Savings' }
                                ].map((item, i) => (
                                    <div key={i} className="group cursor-pointer">
                                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1 block">{item.category}</span>
                                        <h5 className="font-bold text-gray-800 group-hover:text-green-600 transition-colors leading-snug">{item.title}</h5>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-gray-900 rounded-[2rem] p-8 text-white relative overflow-hidden group">
                            <div className="absolute -top-12 -right-12 w-32 h-32 bg-green-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>
                            <h4 className="text-xl font-bold mb-4 relative z-10">Mover?</h4>
                            <p className="text-gray-400 text-sm mb-6 relative z-10 leading-relaxed">
                                Looking to grow your moving business? Join our professional network.
                            </p>
                            <Link to="/resources">
                                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white hover:text-gray-900 relative z-10 rounded-xl">
                                    Mover Hub
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerGuide;
